/**
 * In-memory JWT store. Kept framework-free so the axios interceptor and the
 * socket hook can both read it without React context.
 *
 * Deliberately NOT persisted to localStorage/sessionStorage: an XSS bug can
 * still read this in-memory value while the page is open, but it can't walk
 * away with a durable session the way it could when the token lived in
 * localStorage — it vanishes on reload/tab close, and the token itself is
 * short-lived (see JWT_EXPIRES_IN). The long-lived session lives in an
 * HttpOnly refresh-token cookie that JS can never read at all.
 */
let currentToken: string | null = null;

type Listener = (token: string | null) => void;
const listeners = new Set<Listener>();

export function getToken(): string | null {
  return currentToken;
}

export function setToken(token: string | null): void {
  currentToken = token;
  for (const listener of listeners) listener(token);
}

export function onTokenChange(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Decode (not verify — the server is the only party that needs to verify) the
 * `exp` claim of a JWT, in milliseconds since epoch. Used only to schedule a
 * proactive silent refresh before the token actually expires.
 */
export function getTokenExpiryMs(token: string): number | null {
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const payload = JSON.parse(atob(parts[1])) as { exp?: number };
    return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}
